import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';
import { auth } from '../utils/auth';
import { useLanguage } from '../i18n';
import LeadInfoSections from './lead-detail/LeadInfoSections';
import LeadCalculationResults from './lead-detail/LeadCalculationResults';
import LeadActions from './lead-detail/LeadActions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminLeadDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useLanguage();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailData, setEmailData] = useState({ partner_email: '', partner_lastname: '', partner_gender: 'male' });
  const [showConvertForm, setShowConvertForm] = useState(false);
  const [converting, setConverting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePlz, setDeletePlz] = useState('');
  const [deleting, setDeleting] = useState(false);

  const fetchLead = useCallback(async () => {
    const token = auth.getToken();
    if (!token) { navigate('/admin/login'); return; }
    try {
      setLoading(true);
      const response = await axios.get(`${API}/leads/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setLead(response.data);
    } catch (error) {
      toast.error(t('toastLeadLoadError'));
      navigate('/admin/dashboard');
    } finally {
      setLoading(false);
    }
  }, [id, t, navigate]);

  useEffect(() => { fetchLead(); }, [fetchLead]);

  const updateLead = async (updates) => {
    const token = auth.getToken();
    try {
      setUpdating(true);
      const response = await axios.patch(`${API}/leads/${id}`, updates, { headers: { Authorization: `Bearer ${token}` } });
      setLead(response.data);
      toast.success(t('toastLeadUpdated'));
    } catch (error) {
      toast.error(t('toastUpdateError'));
    } finally {
      setUpdating(false);
    }
  };

  const sendPartnerEmail = async () => {
    const token = auth.getToken();
    try {
      const formData = new FormData();
      formData.append('partner_email', emailData.partner_email);
      formData.append('partner_lastname', emailData.partner_lastname);
      formData.append('partner_gender', emailData.partner_gender);
      await axios.post(`${API}/leads/${id}/email-partner`, formData, { headers: { Authorization: `Bearer ${token}` } });
      await updateLead({ status: 'contacted', assigned_partner: emailData.partner_email });
      toast.success(t('toastEmailSent'));
      setShowEmailForm(false);
      setEmailData({ partner_email: '', partner_lastname: '', partner_gender: 'male' });
    } catch (error) {
      toast.error(t('toastEmailSendError'));
    }
  };

  const convertToRegularLead = async () => {
    if (!window.confirm('Diesen Wartelisten-Eintrag in einen regulären Lead umwandeln?')) return;
    const token = auth.getToken();
    try {
      setConverting(true);
      await axios.post(`${API}/leads/${id}/convert-to-regular`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t('toastLeadConverted'));
      fetchLead();
      setShowConvertForm(false);
    } catch (error) {
      toast.error(t('toastConvertError'));
    } finally {
      setConverting(false);
    }
  };

  const deleteLead = async () => {
    if (deletePlz !== lead.plz) { toast.error(t('toastPlzMismatch')); return; }
    const token = auth.getToken();
    try {
      setDeleting(true);
      await axios.delete(`${API}/leads/${id}?plz_verification=${deletePlz}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t('toastLeadDeleted'));
      navigate('/admin/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('toastLeadDeleteError'));
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString) => new Date(dateString).toLocaleDateString('de-DE', { year: 'numeric', month: 'long', day: '2-digit', hour: '2-digit', minute: '2-digit' });

  const formatCurrency = (amount, currency, locale) => {
    if (locale === 'de-CH') {
      return `${currency} ${amount.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, "'")}`;
    }
    return `${amount.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ".")} ${currency}`;
  };

  // Parse calc_json
  let calcData = null;
  if (lead?.calc_json) {
    try { calcData = typeof lead.calc_json === 'string' ? JSON.parse(lead.calc_json) : lead.calc_json; }
    catch (e) { console.error('Failed to parse calc_json:', e); }
  }

  const leadCurrency = lead?.currency || calcData?.currency || 'CHF';
  const leadLocale = lead?.display_locale || calcData?.display_locale || 'de-CH';

  const convertToDisplayCurrency = (amountEur) => {
    if (leadCurrency === 'CHF') return amountEur / (calcData?.chf_to_eur_rate || 0.95);
    return amountEur;
  };

  const hasCostData = lead?.status !== 'waitlist' && (calcData?.invest_beginn_separatus != null || lead?.invest_separatus_eur != null);

  if (loading) {
    return <div className="admin-container"><div className="admin-content"><div style={{ textAlign: 'center', padding: '2rem' }}>Lade Lead...</div></div></div>;
  }
  if (!lead) return null;

  return (
    <div className="admin-container" data-testid="admin-lead-detail">
      <div className="admin-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <Link to="/admin/dashboard">
            <button className="btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }} data-testid="back-to-dashboard-btn">
              <ArrowLeft size={18} /> Zurück
            </button>
          </Link>
          <h1 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1B5E20' }}>Lead Details</h1>
        </div>
      </div>

      <div className="admin-content">
        <LeadInfoSections lead={lead} formatDate={formatDate} />
        <LeadCalculationResults lead={lead} calcData={calcData} hasCostData={hasCostData} formatCurrency={formatCurrency} convertToDisplayCurrency={convertToDisplayCurrency} leadCurrency={leadCurrency} leadLocale={leadLocale} />
        <LeadActions
          lead={lead} setLead={setLead} updateLead={updateLead} updating={updating}
          showEmailForm={showEmailForm} setShowEmailForm={setShowEmailForm} emailData={emailData} setEmailData={setEmailData} sendPartnerEmail={sendPartnerEmail}
          convertToRegularLead={convertToRegularLead} converting={converting}
          showDeleteModal={showDeleteModal} setShowDeleteModal={setShowDeleteModal} deletePlz={deletePlz} setDeletePlz={setDeletePlz} deleteLead={deleteLead} deleting={deleting}
          navigate={navigate} t={t}
        />
      </div>
    </div>
  );
};

export default AdminLeadDetail;
